import React, { useState, useEffect } from 'react';
import { DonationService } from '../../services/DonationService';
import { ProjectService } from '../../services/ProjectService';
import { showError, logError, logInfo } from '../../utils/errorHandler';
import { formatFullDateTime } from '../../utils/dateFormatter';
import { Donation } from '../../shared/models/Donation';
import { Project } from '../../shared/models/Project';
import { TopBar } from '../../components/TopBar/TopBar';
import './DonationDetailView.css';

interface DonationDetailViewProps {
  donation: Donation;
  onBack: () => void;
  onProjectClick?: (projectId: string) => void;
  onApplyInvoice?: (donation: Donation) => void;
  onViewCertificate?: (donation: Donation) => void;
}

/**
 * 捐赠详情页面
 */
export const DonationDetailView: React.FC<DonationDetailViewProps> = ({
  donation,
  onBack,
  onProjectClick,
  onApplyInvoice,
  onViewCertificate,
}) => {
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [donationDetail, setDonationDetail] = useState<Donation>(donation);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [donation.id]);

  const loadData = async () => {
    setLoading(true);
    try {
      // 加载捐赠详情
      const detail = await DonationService.getDonationById(donation.id);
      setDonationDetail(detail);

      // 加载项目信息
      if (detail.projectId) {
        const projectData = await ProjectService.getProjectById(detail.projectId);
        setProject(projectData);
      }
    } catch (error) {
      logError('[DonationDetailView] Failed to load data', error);
      showError('加载捐赠详情失败', 'DonationDetailView.loadData');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectClick = () => {
    if (donationDetail.projectId && onProjectClick) {
      onProjectClick(donationDetail.projectId);
    }
  };

  const handleCopyOrderNo = () => {
    if (donationDetail.donationOrderNo) {
      navigator.clipboard.writeText(donationDetail.donationOrderNo);
      // 可以显示一个提示
      logInfo('[DonationDetailView] Order number copied', { orderNo: donationDetail.donationOrderNo });
    }
  };

  if (loading) {
    return (
      <div className="donation-detail-view">
        <TopBar title="捐赠详情" showBack onBack={onBack} />
        <div className="donation-detail-loading">加载中...</div>
      </div>
    );
  }

  const displayAmount = donationDetail.donationType === 'MONTHLY' && donationDetail.totalDonationAmount
    ? donationDetail.totalDonationAmount
    : donationDetail.amount;

  const donationTypeText = donationDetail.donationType === 'MONTHLY' ? '月捐' : '单次捐';
  const paymentTime = donationDetail.paymentTime || donationDetail.createTime;

  return (
    <div className="donation-detail-view">
      <TopBar title="捐赠详情" showBack onBack={onBack} />
      
      <div className="donation-detail-content">
        {/* 项目信息 */}
        <div className="donation-detail-project">
          {(project?.coverThumbUrl || project?.coverImageUrl || project?.organization?.logoUrl) && (
            <div className="donation-detail-project-icon">
              <img 
                src={project?.coverThumbUrl || project?.coverImageUrl || project?.organization?.logoUrl || '/placeholder.svg'} 
                alt={project?.title || '项目'} 
                onError={(e) => {
                  e.currentTarget.src = '/placeholder.svg';
                }}
              />
            </div>
          )}
          <div className="donation-detail-project-info">
            <div className="donation-detail-project-name" onClick={handleProjectClick}>
              {project?.title || '未知项目'}
              <span className="arrow">›</span>
            </div>
            <div className="donation-detail-project-amount">¥{displayAmount.toFixed(2)}</div>
          </div>
        </div>

        {/* 交易详情 */}
        <div className="donation-detail-section">
          <div className="donation-detail-section-title">交易详情</div>
          <div className="donation-detail-info-item">
            <span className="donation-detail-info-label">捐款类型</span>
            <span className="donation-detail-info-value">{donationTypeText}</span>
          </div>
          <div className="donation-detail-info-item">
            <span className="donation-detail-info-label">交易时间</span>
            <span className="donation-detail-info-value">{formatFullDateTime(paymentTime)}</span>
          </div>
          <div className="donation-detail-info-item">
            <span className="donation-detail-info-label">订单号</span>
            <span className="donation-detail-info-value donation-detail-order-no" onClick={handleCopyOrderNo}>
              {donationDetail.donationOrderNo || '暂无'}
            </span>
          </div>
          {donationDetail.donationType === 'MONTHLY' && (
            <>
              {donationDetail.monthlyDonationOrderNo && (
                <div className="donation-detail-info-item">
                  <span className="donation-detail-info-label">月捐订单号</span>
                  <span className="donation-detail-info-value">{donationDetail.monthlyDonationOrderNo}</span>
                </div>
              )}
              {donationDetail.deductionCount !== undefined && (
                <div className="donation-detail-info-item">
                  <span className="donation-detail-info-label">已扣款次数</span>
                  <span className="donation-detail-info-value">{donationDetail.deductionCount}次</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* 操作按钮 */}
        <div className="donation-detail-actions">
          {onApplyInvoice && (
            <button
              className="donation-detail-action-btn donation-detail-action-btn-secondary"
              onClick={() => onApplyInvoice(donationDetail)}
            >
              申请开票
            </button>
          )}
          {onViewCertificate && (
            <button
              className="donation-detail-action-btn donation-detail-action-btn-primary"
              onClick={() => onViewCertificate(donationDetail)}
            >
              查看证书
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

